import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Center,
  ComboboxItem,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDocumentTitle } from "@mantine/hooks";
import { IconMailCheck } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";

import { getRolesValues } from "@data/dropdownValues";

import { PAGE_TITLE } from "@constants/index";

import { useInviteUser } from "@hooks/mutations/user/useInviteUser";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { useAuth } from "@contexts/auth";

import { StyledInviteUser } from "./InviteUser.styled";
import { OnboardingInviteModal } from "./components/OnboardingInviteModal";

const schema = () =>
  z.object({
    email: z
      .string()
      .email(i18n.t("email-invalid"))
      .min(1, i18n.t("email-required")),
    partner_id: z.string().optional(),
  });
type Inputs = z.infer<ReturnType<typeof schema>>;

export const InviteUser = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("invite-user")} • ${PAGE_TITLE}`);
  const { id: userId, can } = useAuth();
  const canManageAccess = can("access.manage");

  const { data: partnersData } = useGetPartners({
    isActive: true,
    isEnabled: canManageAccess,
  });

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  const [roles, setRoles] = useState<ComboboxItem[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(schema()),
    initialValues: {
      email: "",
      partner_id: "",
    },
  });

  const { mutate: inviteUser, isLoading: isInviteUserLoading } =
    useInviteUser();

  const handleFormSubmit = (values: Inputs) => {
    inviteUser(
      {
        email: values.email,
        inviter: userId,
        roles: [...roles.map(role => role.value)],
        partner_id: canManageAccess ? values.partner_id || undefined : undefined,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setRoles([]);
          form.reset();
        },
      }
    );
  };

  return (
    <StyledInviteUser>
      <Group align="flex-start" gap="xl">
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          {!showSuccess ? (
            <>
              <div className="form-field">
                <TextInput
                  label={t("email")}
                  withAsterisk
                  {...form.getInputProps("email")}
                  key={form.key("email")}
                  placeholder={t("eg", { example: "johndoe@gmail.com" })}
                />
              </div>

              {canManageAccess && <div className="form-field">
                <Select
                  label={t("partner")}
                  placeholder={t("select-partner")}
                  data={partnersOptions}
                  searchable
                  clearable
                  {...form.getInputProps("partner_id")}
                />
              </div>}

              <div className="form-field">
                <MultiSelectWithSearch
                  label={t("role")}
                  placeholder={t("select-role")}
                  data={getRolesValues().filter(role => canManageAccess || ["doctor", "nurse", "receptionist"].includes(role.value))}
                  value={roles}
                  setValue={setRoles}
                  required
                  searchable={false}
                  maxDisplayedValues={3}
                />
              </div>

              <Button
                loading={isInviteUserLoading}
                mt={"lg"}
                type="submit"
                disabled={!roles.length}>
                {t("invite-user")}
              </Button>
            </>
          ) : (
            <Stack mt="xl" gap="0">
              <Center>
                <Group
                  justify="center"
                  bg="blue.0"
                  w={80}
                  h={80}
                  style={{ borderRadius: "100%" }}>
                  <IconMailCheck size={40} color="var(--clr-blue-400)" />
                </Group>
              </Center>
              <Title order={3} ta="center" mt="md" fw={600}>
                {t("invitation-sent")}
              </Title>
              <Text ta="center" c="dimmed" mt="xs">
                {t("invitation-sent-description")}
              </Text>
              <Button
                variant="outline"
                mt="xl"
                onClick={() => setShowSuccess(false)}>
                {t("go-back")}
              </Button>
            </Stack>
          )}
        </form>

        <Stack className="form" gap="md" style={{ flex: 1, maxWidth: "400px", background: "#f8f9fa", borderStyle: "dashed" }}>
          <Title order={4} fw={600} c="dimmed">Invite via Social Media</Title>
          <Text size="sm" c="dimmed">
            Don't have their email? Create a secure onboarding link using their unique social media username (e.g. TikTok, Instagram) and send it to them directly via DM.
          </Text>
          <Group mt="md">
            <OnboardingInviteModal />
          </Group>
        </Stack>
      </Group>
    </StyledInviteUser>
  );
};
